/**
 * Single-scroll ticket detail panel — no tabs, no save buttons.
 *
 * Layout (top-to-bottom, one continuous scroll):
 *   1. Header        : back link · publicCode · status dropdown · title · created-at
 *   2. Contact strip : caller name / phone / email / call-id
 *   3. Description   : the actual reason the ticket exists
 *   4. Meta row      : priority + category + due-date selects (auto-save)
 *   5. Big CTA       : "Ticket abschliessen" / "Wieder öffnen"
 *   6. Comments      : inline list + reply form
 *   7. Anruf-Log     : transcript when ticket.callId is set
 *
 * All field edits autosave through the existing `useUpdateTicket()`
 * mutation — no save button, no dirty banner. Status changes also
 * autosave; the big CTA at the bottom is a one-click shortcut to
 * `closed` (or back to `open` for already-closed tickets).
 */
import * as React from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Mail,
  Phone,
  PhoneCall,
  RotateCcw,
  Send,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input, Textarea } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useToast } from '../../components/ui/toast';
import {
  tsToDate,
  useAddTicketComment,
  useTicketComments,
  useUpdateTicket,
  type TicketRow,
  type TicketCategory,
  type TicketPriority,
  type TicketStatus,
} from '../../hooks/useTickets';
import { TicketCallLogPanel } from '../../components/TicketCallLogPanel';
import { cn } from '../../lib/utils';

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: 'Offen',
  'in-progress': 'In Bearbeitung',
  'waiting-customer': 'Warten auf Kunde',
  deferred: 'Zurückgestellt',
  resolved: 'Gelöst',
  closed: 'Geschlossen',
};

interface Props {
  ticket: TicketRow;
  /** Mobile only — used to render a back chevron above the title. */
  onBack?: () => void;
}

export function TicketDetailPanel({ ticket, onBack }: Props) {
  const update = useUpdateTicket();
  const addComment = useAddTicketComment();
  const comments = useTicketComments(ticket.id);
  const { toast } = useToast();

  const [comment, setComment] = React.useState('');
  const [sendToCustomer, setSendToCustomer] = React.useState(false);
  const [editStatus, setEditStatus] = React.useState<TicketStatus>(ticket.status);
  const [editPriority, setEditPriority] = React.useState<TicketPriority>(ticket.priority);
  const [editCategory, setEditCategory] = React.useState<TicketCategory>(ticket.category);
  const [editDue, setEditDue] = React.useState(toLocalDateInput(tsToDate(ticket.dueAt ?? null)));

  // Reset local edit-state whenever the active ticket changes. Without
  // this, switching tickets would briefly flash the previous values
  // until React Query re-renders.
  React.useEffect(() => {
    setEditStatus(ticket.status);
    setEditPriority(ticket.priority);
    setEditCategory(ticket.category);
    setEditDue(toLocalDateInput(tsToDate(ticket.dueAt ?? null)));
    setComment('');
    setSendToCustomer(false);
  }, [ticket.id, ticket.status, ticket.priority, ticket.category, ticket.dueAt]);

  // ── Autosave helpers ────────────────────────────────────────────────
  // Each field edit fires the mutation immediately. Errors roll the
  // local state back so the UI can never lie about the persisted value.

  async function persist(
    patch: Parameters<typeof update.mutateAsync>[0],
    rollback: () => void,
    successTitle?: string,
  ): Promise<void> {
    try {
      await update.mutateAsync(patch);
      if (successTitle) toast({ title: successTitle });
    } catch (err) {
      rollback();
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  function onChangeStatus(next: TicketStatus) {
    const prev = editStatus;
    setEditStatus(next);
    void persist({ ticketId: ticket.id, status: next }, () => setEditStatus(prev));
  }
  function onChangePriority(next: TicketPriority) {
    const prev = editPriority;
    setEditPriority(next);
    void persist({ ticketId: ticket.id, priority: next }, () => setEditPriority(prev));
  }
  function onChangeCategory(next: TicketCategory) {
    const prev = editCategory;
    setEditCategory(next);
    void persist({ ticketId: ticket.id, category: next }, () => setEditCategory(prev));
  }
  function onChangeDue(next: string) {
    const prev = editDue;
    setEditDue(next);
    void persist(
      {
        ticketId: ticket.id,
        dueAtMs: next ? new Date(next).getTime() : null,
      },
      () => setEditDue(prev),
    );
  }

  async function onAddComment() {
    if (!comment.trim()) return;
    try {
      await addComment.mutateAsync({
        ticketId: ticket.id,
        body: comment.trim(),
        sendToCustomer,
      });
      setComment('');
      setSendToCustomer(false);
      toast({
        title: sendToCustomer
          ? 'Kommentar gespeichert + Kunde benachrichtigt'
          : 'Kommentar gespeichert',
      });
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  const due = tsToDate(ticket.dueAt);
  const overdue =
    due != null
    && due.getTime() < Date.now()
    && ticket.status !== 'resolved'
    && ticket.status !== 'closed';
  const created = tsToDate(ticket.createdAt);
  const hasCall = !!ticket.callId;
  const isClosed = ticket.status === 'closed' || ticket.status === 'resolved';

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-3xl space-y-5 px-4 py-4 sm:px-6 sm:py-5">
          {/* ── Header row ── */}
          {onBack && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="-ml-2 h-7 px-2 lg:hidden"
            >
              <ArrowLeft className="mr-1 size-3.5" />
              Zurück zur Liste
            </Button>
          )}

          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[11px] uppercase text-muted-foreground">
                  {ticket.publicCode}
                </span>
                {overdue && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-destructive">
                    <AlertTriangle className="size-2.5" /> überfällig
                  </span>
                )}
              </div>
              <h2 className="mt-0.5 text-base font-semibold leading-tight sm:text-lg">
                {ticket.subject}
              </h2>
              {created && (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Erstellt{' '}
                  {created.toLocaleString('de-DE', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </p>
              )}
            </div>
            <div className="w-[180px] shrink-0">
              <Select value={editStatus} onValueChange={(v) => onChangeStatus(v as TicketStatus)}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Offen</SelectItem>
                  <SelectItem value="in-progress">In Bearbeitung</SelectItem>
                  <SelectItem value="waiting-customer">Warten auf Kunde</SelectItem>
                  <SelectItem value="deferred">Zurückgestellt</SelectItem>
                  <SelectItem value="resolved">Gelöst</SelectItem>
                  <SelectItem value="closed">Geschlossen</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ── Contact strip — name + phone + email + call-id ── */}
          <ContactStrip ticket={ticket} />

          {/* ── Description (the WHAT) ── */}
          <section className="space-y-2">
            <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Beschreibung
            </h3>
            <pre className="whitespace-pre-wrap rounded-md bg-muted/30 p-3 font-sans text-sm">
              {ticket.description}
            </pre>
            {ticket.callSummary && (
              <pre className="mt-2 whitespace-pre-wrap rounded-md bg-muted/20 p-3 font-sans text-xs">
                <span className="block pb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                  Gesprächs-Zusammenfassung
                </span>
                {ticket.callSummary}
              </pre>
            )}
          </section>

          {/* ── Meta row — autosave dropdowns ── */}
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Priorität</Label>
              <Select
                value={editPriority}
                onValueChange={(v) => onChangePriority(v as TicketPriority)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="urgent">Dringend</SelectItem>
                  <SelectItem value="high">Hoch</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Niedrig</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Kategorie</Label>
              <Select
                value={editCategory}
                onValueChange={(v) => onChangeCategory(v as TicketCategory)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="support">Support</SelectItem>
                  <SelectItem value="billing">Abrechnung</SelectItem>
                  <SelectItem value="technical">Technisch</SelectItem>
                  <SelectItem value="complaint">Beschwerde</SelectItem>
                  <SelectItem value="sales">Vertrieb</SelectItem>
                  <SelectItem value="feedback">Feedback</SelectItem>
                  <SelectItem value="general">Allgemein</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Fällig bis</Label>
              <Input
                type="datetime-local"
                value={editDue}
                onChange={(e) => onChangeDue(e.target.value)}
              />
            </div>
          </div>

          {/* ── Big CTA — close / re-open ── */}
          {isClosed ? (
            <Button
              size="lg"
              variant="outline"
              className="h-12 w-full"
              onClick={() => onChangeStatus('open')}
              disabled={update.isPending}
            >
              {update.isPending ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <RotateCcw className="mr-2 size-4" />
              )}
              Ticket wieder öffnen
            </Button>
          ) : (
            <Button
              size="lg"
              className="h-12 w-full bg-emerald-600 text-white hover:bg-emerald-600/90"
              onClick={() => onChangeStatus('closed')}
              disabled={update.isPending}
            >
              {update.isPending ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-2 size-4" />
              )}
              Ticket abschließen
            </Button>
          )}

          {/* ── Comments inline ── */}
          <section className="space-y-3 border-t border-border pt-5">
            <h3 className="text-sm font-medium">
              Kommentare
              {comments.data && comments.data.length > 0 && (
                <span className="ml-2 rounded-full bg-secondary px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
                  {comments.data.length}
                </span>
              )}
            </h3>
            {comments.isLoading ? (
              <div className="text-xs text-muted-foreground">Lade …</div>
            ) : (comments.data ?? []).length === 0 ? (
              <div className="rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
                Noch keine Kommentare.
              </div>
            ) : (
              <ul className="space-y-2">
                {(comments.data ?? []).map((c) => {
                  const ts = tsToDate(c.createdAt);
                  return (
                    <li key={c.id} className="rounded-md border border-border p-3 text-sm">
                      <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                        <span>{c.authorLabel}</span>
                        <span>
                          {ts?.toLocaleString('de-DE', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                          {c.emailedToCustomer && (
                            <span className="ml-2 inline-flex items-center gap-1 rounded bg-primary/10 px-1 py-0.5 text-[10px] text-primary">
                              <Mail className="size-3" /> per E-Mail an Kunde
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="whitespace-pre-wrap">{c.body}</div>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="space-y-2">
              <Textarea
                placeholder="Kommentar / Antwort verfassen …"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
              />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label
                  className={cn(
                    'flex items-center gap-2 text-xs',
                    !ticket.callerEmail && 'opacity-50',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={sendToCustomer}
                    onChange={(e) => setSendToCustomer(e.target.checked)}
                    disabled={!ticket.callerEmail}
                  />
                  Per E-Mail an {ticket.callerEmail ?? 'Kunde'} senden
                </label>
                <Button
                  size="sm"
                  onClick={() => void onAddComment()}
                  disabled={addComment.isPending || !comment.trim()}
                >
                  {addComment.isPending ? (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  ) : (
                    <Send className="mr-2 size-4" />
                  )}
                  Senden
                </Button>
              </div>
            </div>
          </section>

          {/* ── Anruf-Log inline (only when there is one) ── */}
          {hasCall && (
            <section className="space-y-3 border-t border-border pt-5">
              <h3 className="flex items-center gap-2 text-sm font-medium">
                <PhoneCall className="size-3.5" /> Anruf-Log
              </h3>
              <TicketCallLogPanel callId={ticket.callId!} />
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Contact strip ───────────────────────────────────────────────────────

function ContactStrip({ ticket }: { ticket: TicketRow }) {
  const items: Array<{ key: string; node: React.ReactNode }> = [];
  if (ticket.callerName) {
    items.push({ key: 'name', node: <span className="font-medium text-foreground">{ticket.callerName}</span> });
  }
  if (ticket.callerPhone) {
    items.push({
      key: 'phone',
      node: (
        <a href={`tel:${ticket.callerPhone}`} className="inline-flex items-center gap-1 hover:text-foreground">
          <Phone className="size-3" /> {ticket.callerPhone}
        </a>
      ),
    });
  }
  if (ticket.callerEmail) {
    items.push({
      key: 'email',
      node: (
        <a href={`mailto:${ticket.callerEmail}`} className="inline-flex items-center gap-1 hover:text-foreground">
          <Mail className="size-3" /> {ticket.callerEmail}
        </a>
      ),
    });
  }
  if (ticket.callId) {
    items.push({
      key: 'callid',
      node: (
        <span className="font-mono text-[11px]">Anruf-ID: {ticket.callId.slice(0, 8)}</span>
      ),
    });
  }
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 rounded-md bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
      {items.map((i) => (
        <span key={i.key}>{i.node}</span>
      ))}
    </div>
  );
}

function toLocalDateInput(d: Date | null): string {
  if (!d) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

// `STATUS_LABEL` is exported indirectly via the Select items — but the
// constant can still be useful for log toasts elsewhere on this page.
void STATUS_LABEL;
